import { useMemo } from "react";
import { Link } from "react-router-dom";
import { mockAvailability, mockWarehousePrices, WAREHOUSES } from "../lib/mockInventory";
import { effectivePrice } from "../lib/pricing";
import { variantSku } from "../lib/variant";
import { useCurrency } from "../context/CurrencyContext";
import { useOrderDraft, lineKey } from "../context/OrderDraftContext";
import QtyStepper from "./QtyStepper";
import styles from "./OrderSheetItem.module.css";

// One catalog item rendered as a row group on the OrderSheet.
//
// Outer grid: [item meta | US column | INTL column] — matches the legend
// header in OrderSheet so warehouse labels line up with their data.
// Each color × size variant is a sub-row; the variant name spans the two
// warehouse columns above its pair of cells.
export default function OrderSheetItem({ item, user, effectiveMode }) {
  const { formatAmount } = useCurrency();
  const { draft, setLine } = useOrderDraft();

  const variants = useMemo(() => buildVariants(item), [item]);

  const itemRollup = useMemo(() => {
    let qty = 0;
    let amount = 0;
    for (const v of variants) {
      const sku = variantSku(item, v.color, v.size);
      const priced = effectivePrice(item, user, { color: v.color, siteMode: effectiveMode });
      const baseAmount = priced?.price?.amount ?? 0;
      const wprices = mockWarehousePrices({ basePrice: baseAmount, currency: priced?.price?.currency || "USD" });
      for (const w of WAREHOUSES) {
        const k = lineKey({ sku, warehouse: w.id });
        const q = draft[k]?.qty || 0;
        if (q > 0) { qty += q; amount += q * (wprices[w.id]?.amount || 0); }
      }
    }
    return { qty, amount };
  }, [item, variants, draft, user, effectiveMode]);

  return (
    <section className={[styles.row, itemRollup.qty > 0 ? styles.rowActive : ""].join(" ")}>
      <header className={styles.head}>
        <Link to={`/product/${encodeURIComponent(item.id ?? item.sku)}`} className={styles.name}>
          {item.name}
        </Link>
        <div className={styles.sub}>
          <span>{item.sku || item.id}</span>
          {item.productLine && <span>· {item.productLine}</span>}
          {item.volume ? <span>· {item.volume} L</span> : null}
        </div>
        <Link to={`/product/${encodeURIComponent(item.id ?? item.sku)}`} className={styles.thumbWrap}>
          <img src={item.image} alt="" className={styles.thumb} loading="lazy" />
        </Link>
        {itemRollup.qty > 0 && (
          <div className={styles.itemRollup} aria-live="polite">
            <span>{itemRollup.qty}u</span>
            <strong>{formatAmount(itemRollup.amount)}</strong>
          </div>
        )}
      </header>

      {variants.map((v) => {
        const priced = effectivePrice(item, user, { color: v.color, siteMode: effectiveMode });
        const baseAmount = priced?.price?.amount ?? 0;
        const currency = priced?.price?.currency ?? "USD";
        const sku = variantSku(item, v.color, v.size);
        const stock = mockAvailability(sku, "b2b");
        const stockByWh = Object.fromEntries(
          (stock?.warehouses || []).map((w) => [w.id, w.available])
        );
        const wprices = mockWarehousePrices({ basePrice: baseAmount, currency });
        const variantQty = WAREHOUSES.reduce((n, w) => {
          const k = lineKey({ sku, warehouse: w.id });
          return n + (draft[k]?.qty || 0);
        }, 0);

        const variantAmount = WAREHOUSES.reduce((n, w) => {
          const k = lineKey({ sku, warehouse: w.id });
          const q = draft[k]?.qty || 0;
          return n + q * (wprices[w.id]?.amount || 0);
        }, 0);

        return (
          <div key={sku} className={styles.variantRow}>
            <div className={[styles.variantMeta, variantQty > 0 ? styles.variantMetaActive : ""].join(" ")}>
              <span className={styles.swatch} style={swatchStyle(v.color)} aria-hidden />
              <span className={styles.variantName}>
                {v.color?.name || "—"}
                {v.size ? <span className={styles.size}> · {v.size}</span> : null}
              </span>
            </div>

            <div
              className={[styles.totalCell, variantQty > 0 ? styles.totalCellActive : styles.totalCellEmpty].join(" ")}
              aria-live="polite"
            >
              {variantQty > 0 && (
                <>
                  <div className={styles.totalQty}>{variantQty} u</div>
                  <div className={styles.totalAmount}>{formatAmount(variantAmount)}</div>
                </>
              )}
            </div>

            {WAREHOUSES.map((w) => {
              const key = lineKey({ sku, warehouse: w.id });
              const qty = draft[key]?.qty || 0;
              const stockN = stockByWh[w.id];
              const price = wprices[w.id];
              return (
                <div
                  key={w.id}
                  className={[styles.cell, qty > 0 ? styles.cellActive : ""].join(" ")}
                >
                  <div className={styles.cellLabel} aria-hidden>
                    <span>{w.flag}</span> {w.short}
                  </div>
                  <div className={styles.cellLine}>
                    <span className={styles.price}>{formatAmount(price.amount)}</span>
                    <QtyStepper
                      value={qty}
                      max={stockN ?? undefined}
                      size="sm"
                      onChange={(n) =>
                        setLine(key, n > 0 ? {
                          sku,
                          itemId: item.id ?? item.sku,
                          itemName: item.name,
                          colorId: v.color?.id,
                          colorName: v.color?.name,
                          size: v.size,
                          warehouse: w.id,
                          warehouseLabel: w.label,
                          qty: n,
                          unitPrice: price.amount,
                          currency: price.currency,
                          image: v.color?.image || item.image,
                        } : null)
                      }
                      disabled={stockN === 0}
                    />
                  </div>
                  <div className={[styles.stock, styles[qtyTone(stockN)]].join(" ")}>
                    {stockN == null ? "—" :
                     stockN <= 0 ? "Out of stock" :
                     stockN <= 5 ? `${stockN} left` :
                     `${stockN} in stock`}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </section>
  );
}

function buildVariants(item) {
  const colors = item.colors?.length ? item.colors : [{ id: "default", name: "Standard" }];
  const sizes = item.sizes?.length ? item.sizes : [null];
  const out = [];
  for (const color of colors) {
    for (const size of sizes) out.push({ color, size });
  }
  return out;
}

function swatchStyle(c) {
  if (!c) return { background: "#ccc" };
  if (c.image) return { backgroundImage: `url(${c.image})`, backgroundSize: "cover" };
  return { background: c.hex || "#ccc" };
}

function qtyTone(n) {
  if (n == null) return "unknown";
  if (n <= 0) return "out";
  if (n <= 5) return "low";
  return "ok";
}
